import { HelpTooltip } from '../../shared/components/HelpTooltip';

/** Plain-language help for the payroll tax terms used on the Tax Settings screens. No internal codes, no rates, no legal advice. */
export const TAX_TERM_HELP = {
  sui: {
    term: 'State unemployment insurance (SUI)',
    text: 'A tax employers pay to a state to fund unemployment benefits. It is set by the state where the employee works and by your company\'s own account and rate with that state. It is separate from the federal unemployment tax (FUTA).',
  },
  futa: {
    term: 'Federal unemployment tax (FUTA)',
    text: 'A federal tax employers pay for unemployment programs. It is different from state unemployment insurance (SUI). EPN does not set up FUTA on this screen.',
  },
  withholdingAccount: {
    term: 'State withholding account',
    text: 'The account number a state gave your company so it can report and pay the income tax withheld from employees. It is on the registration letter from the state tax agency.',
  },
  employerTaxRate: {
    term: 'Employer tax rate',
    text: 'The percentage a state tells your company to use for its own unemployment tax. It is usually printed on the rate notice the state agency mails each year. EPN does not choose or guess it.',
  },
  taxableWageBase: {
    term: 'Taxable wage base',
    text: 'The most each employee earns in a year that a tax applies to. Once an employee passes it, no more of that tax is due for the year. The agency sets it and it can change.',
  },
  reciprocity: {
    term: 'Reciprocity',
    text: 'An agreement between two states that lets an employee who lives in one state and works in the other skip income tax in the work state. It usually needs a signed form from the employee. It applies only to specific pairs of states.',
  },
  residentTax: {
    term: 'Resident tax',
    text: 'Income tax a state or city charges people who live there, based on the home address. It can apply even when the employee works somewhere else.',
  },
  workLocationTax: {
    term: 'Work-location tax',
    text: 'Tax a state or city charges for work done inside its borders, based on where the employee works. It can apply even when the employee lives somewhere else.',
  },
  privatePlan: {
    term: 'Private plan',
    text: 'In a few states an employer may use an approved private insurance plan instead of the state\'s paid leave or disability program. It needs the state\'s approval. EPN does not record private plans yet.',
  },
  depositSchedule: {
    term: 'Deposit schedule',
    text: 'How often your company must send withheld tax to the agency, for example monthly or quarterly. The agency assigns it in a notice. EPN does not make deposits yet.',
  },
  filingFrequency: {
    term: 'Filing frequency',
    text: 'How often your company must send the agency its tax return or report. The agency sets it, and it can differ from the deposit schedule. EPN does not file returns yet.',
  },
} as const;

export type TaxTerm = keyof typeof TAX_TERM_HELP;

/** The small "i" help trigger for one tax term. */
export function TaxTermHelp({ term }: { term: TaxTerm }) {
  const help = TAX_TERM_HELP[term];
  return <HelpTooltip label={`About ${help.term}`} text={help.text} />;
}
